import React, { useEffect, useState } from 'react';
import AdminLayout from './AdminLayout';
import api from '../../api/axios';
import { Download, BarChart2, Users, FolderOpen, AlertTriangle } from 'lucide-react';

const STATUS_COLORS = { yapilacak: '#6b7280', 'devam-ediyor': '#007aff', incelemede: '#ff9500', tamamlandi: '#34c759' };
const STATUS_LABELS = { yapilacak: 'Yapılacak', 'devam-ediyor': 'Devam Ediyor', incelemede: 'İncelemede', tamamlandi: 'Tamamlandı' };
const PRIORITY_COLORS = { kritik: '#ef4444', yuksek: '#f97316', normal: '#007aff', dusuk: '#9ca3af' };
const PRIORITY_LABELS = { kritik: 'Kritik', yuksek: 'Yüksek', normal: 'Normal', dusuk: 'Düşük' };

export default function AdminTaskReports() {
  const [stats, setStats] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get('/tasks/stats'),
      api.get('/tasks/tasks'),
      api.get('/tasks/projects'),
    ]).then(([s, t, p]) => {
      setStats(s.data);
      setTasks(t.data);
      setProjects(p.data);
      setLoading(false);
    });
  }, []);

  if (loading) return <AdminLayout><div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>Yükleniyor...</div></AdminLayout>;

  const { tasks: taskStats } = stats;
  const completionRate = taskStats.total > 0 ? Math.round((taskStats.completed / taskStats.total) * 100) : 0;

  // Proje bazlı istatistikler
  const projectStats = projects.map(p => {
    const pTasks = tasks.filter(t => t.project_id === p.id);
    const completed = pTasks.filter(t => t.status === 'tamamlandi').length;
    const overdue = pTasks.filter(t => t.is_overdue).length;
    return { ...p, total: pTasks.length, completed, overdue, rate: pTasks.length > 0 ? Math.round(completed / pTasks.length * 100) : 0 };
  }).sort((a, b) => b.total - a.total);

  // Kullanıcı bazlı istatistikler
  const userMap = {};
  tasks.forEach(t => {
    if (!t.assigned_to || !t.assigned_name) return;
    if (!userMap[t.assigned_to]) userMap[t.assigned_to] = { name: t.assigned_name, total: 0, completed: 0, overdue: 0 };
    userMap[t.assigned_to].total++;
    if (t.status === 'tamamlandi') userMap[t.assigned_to].completed++;
    if (t.is_overdue) userMap[t.assigned_to].overdue++;
  });
  const userStats = Object.values(userMap).map(u => ({ ...u, rate: u.total > 0 ? Math.round(u.completed / u.total * 100) : 0 })).sort((a, b) => b.total - a.total);

  const exportCSV = () => {
    const headers = ['Görev', 'Proje', 'Durum', 'Öncelik', 'Atanan', 'Teslim', 'İlerleme', 'Gecikiyor'];
    const rows = tasks.map(t => [t.title, t.project_name || '', STATUS_LABELS[t.status] || t.status, PRIORITY_LABELS[t.priority] || t.priority, t.assigned_name || '', t.due_date ? new Date(t.due_date).toLocaleDateString('tr-TR') : '', t.progress + '%', t.is_overdue ? 'Evet' : 'Hayır']);
    const csv = [headers, ...rows].map(r => r.map(v => `"${v}"`).join(',')).join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'gorev-raporu.csv'; a.click();
  };

  return (
    <AdminLayout>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h2 style={{ fontWeight: 700, fontSize: 22, color: '#1a1a2e', display: 'flex', alignItems: 'center', gap: 10 }}>
          <BarChart2 size={22} color="#007aff" /> Görev Raporları
        </h2>
        <button onClick={exportCSV} style={{ background: '#34c759', color: 'white', border: 'none', borderRadius: 10, padding: '10px 18px', cursor: 'pointer', fontWeight: 600, fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
          <Download size={15} /> CSV İndir
        </button>
      </div>

      {/* Özet kartlar */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 10, marginBottom: 20 }}>
        {[
          { label: 'Toplam', value: taskStats.total, color: '#374151', bg: 'rgba(0,0,0,0.05)' },
          { label: 'Yapılacak', value: taskStats.todo, color: '#6b7280', bg: 'rgba(107,114,128,0.08)' },
          { label: 'Devam', value: taskStats.inprogress, color: '#007aff', bg: 'rgba(0,122,255,0.08)' },
          { label: 'Tamamlandı', value: taskStats.completed, color: '#34c759', bg: 'rgba(52,199,89,0.08)' },
          { label: 'Geciken', value: taskStats.overdue, color: '#ef4444', bg: 'rgba(239,68,68,0.08)' },
          { label: 'Tamamlanma', value: completionRate + '%', color: completionRate >= 70 ? '#34c759' : '#f97316', bg: 'rgba(0,0,0,0.04)' },
        ].map(({ label, value, color, bg }) => (
          <div key={label} style={{ background: bg, borderRadius: 12, padding: '14px', textAlign: 'center' }}>
            <div style={{ fontSize: 24, fontWeight: 800, color, lineHeight: 1.2 }}>{value}</div>
            <div style={{ fontSize: 11, color: '#6b7280', marginTop: 3 }}>{label}</div>
          </div>
        ))}
      </div>

      {/* Durum & Öncelik dağılımı */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
        {/* Durum dağılımı */}
        <div style={{ background: 'rgba(255,255,255,0.85)', borderRadius: 14, padding: '18px 20px', boxShadow: '0 2px 10px rgba(0,0,0,0.05)' }}>
          <div style={{ fontWeight: 700, fontSize: 14, color: '#1a1a2e', marginBottom: 14 }}>Duruma Göre Dağılım</div>
          {Object.entries(STATUS_LABELS).map(([key, label]) => {
            const count = tasks.filter(t => t.status === key).length;
            const pct = taskStats.total > 0 ? Math.round(count / taskStats.total * 100) : 0;
            return (
              <div key={key} style={{ marginBottom: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5, fontSize: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <div style={{ width: 10, height: 10, borderRadius: 3, background: STATUS_COLORS[key] }} />
                    <span style={{ fontWeight: 500, color: '#374151' }}>{label}</span>
                  </div>
                  <span style={{ fontWeight: 700, color: STATUS_COLORS[key] }}>{count} (%{pct})</span>
                </div>
                <div style={{ height: 6, background: '#f0f0f0', borderRadius: 4 }}>
                  <div style={{ height: '100%', width: pct + '%', background: STATUS_COLORS[key], borderRadius: 4 }} />
                </div>
              </div>
            );
          })}
        </div>

        {/* Öncelik dağılımı */}
        <div style={{ background: 'rgba(255,255,255,0.85)', borderRadius: 14, padding: '18px 20px', boxShadow: '0 2px 10px rgba(0,0,0,0.05)' }}>
          <div style={{ fontWeight: 700, fontSize: 14, color: '#1a1a2e', marginBottom: 14 }}>Önceliğe Göre Dağılım</div>
          {Object.entries(PRIORITY_LABELS).map(([key, label]) => {
            const count = tasks.filter(t => t.priority === key).length;
            const pct = taskStats.total > 0 ? Math.round(count / taskStats.total * 100) : 0;
            return (
              <div key={key} style={{ marginBottom: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5, fontSize: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <div style={{ width: 10, height: 10, borderRadius: '50%', background: PRIORITY_COLORS[key] }} />
                    <span style={{ fontWeight: 500, color: '#374151' }}>{label}</span>
                  </div>
                  <span style={{ fontWeight: 700, color: PRIORITY_COLORS[key] }}>{count} (%{pct})</span>
                </div>
                <div style={{ height: 6, background: '#f0f0f0', borderRadius: 4 }}>
                  <div style={{ height: '100%', width: pct + '%', background: PRIORITY_COLORS[key], borderRadius: 4 }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Proje performansı */}
      <div style={{ background: 'rgba(255,255,255,0.85)', borderRadius: 14, overflow: 'hidden', boxShadow: '0 2px 10px rgba(0,0,0,0.05)', marginBottom: 14 }}>
        <div style={{ padding: '14px 20px', borderBottom: '1px solid rgba(0,0,0,0.05)', display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700, fontSize: 14, color: '#1a1a2e' }}>
          <FolderOpen size={15} color="#007aff" /> Proje Performansı
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'rgba(0,0,0,0.02)' }}>
                {['PROJE', 'TOPLAM', 'TAMAMLANAN', 'GECİKEN', 'TAMAMLANMA', 'İLERLEME'].map(h => (
                  <th key={h} style={{ padding: '10px 16px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: '#9ca3af' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {projectStats.map(p => (
                <tr key={p.id} style={{ borderTop: '1px solid rgba(0,0,0,0.04)' }}>
                  <td style={{ padding: '10px 16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{ width: 10, height: 10, borderRadius: 3, background: p.color, flexShrink: 0 }} />
                      <span style={{ fontWeight: 600, fontSize: 13, color: '#1a1a2e' }}>{p.name}</span>
                    </div>
                  </td>
                  <td style={{ padding: '10px 16px', textAlign: 'center', fontWeight: 700 }}>{p.total}</td>
                  <td style={{ padding: '10px 16px', textAlign: 'center' }}><span style={{ color: '#34c759', fontWeight: 700 }}>{p.completed}</span></td>
                  <td style={{ padding: '10px 16px', textAlign: 'center' }}><span style={{ color: p.overdue > 0 ? '#ef4444' : '#9ca3af', fontWeight: 700 }}>{p.overdue}</span></td>
                  <td style={{ padding: '10px 16px', textAlign: 'center' }}><span style={{ fontWeight: 800, color: p.rate >= 70 ? '#34c759' : p.rate >= 40 ? '#f97316' : '#ef4444' }}>%{p.rate}</span></td>
                  <td style={{ padding: '10px 16px', minWidth: 100 }}>
                    <div style={{ height: 6, background: '#f0f0f0', borderRadius: 4 }}>
                      <div style={{ height: '100%', width: p.rate + '%', background: p.color, borderRadius: 4 }} />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Kullanıcı performansı */}
      <div style={{ background: 'rgba(255,255,255,0.85)', borderRadius: 14, overflow: 'hidden', boxShadow: '0 2px 10px rgba(0,0,0,0.05)' }}>
        <div style={{ padding: '14px 20px', borderBottom: '1px solid rgba(0,0,0,0.05)', display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700, fontSize: 14, color: '#1a1a2e' }}>
          <Users size={15} color="#007aff" /> Kullanıcı Performansı
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'rgba(0,0,0,0.02)' }}>
                {['KULLANICI', 'TOPLAM', 'TAMAMLANAN', 'GECİKEN', 'BAŞARI ORANI'].map(h => (
                  <th key={h} style={{ padding: '10px 16px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: '#9ca3af' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {userStats.map((u, i) => (
                <tr key={i} style={{ borderTop: '1px solid rgba(0,0,0,0.04)' }}>
                  <td style={{ padding: '10px 16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'linear-gradient(135deg, #007aff, #5856d6)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 11, flexShrink: 0 }}>{u.name.charAt(0).toUpperCase()}</div>
                      <span style={{ fontWeight: 600, fontSize: 13 }}>{u.name}</span>
                    </div>
                  </td>
                  <td style={{ padding: '10px 16px', textAlign: 'center', fontWeight: 700 }}>{u.total}</td>
                  <td style={{ padding: '10px 16px', textAlign: 'center' }}><span style={{ color: '#34c759', fontWeight: 700 }}>{u.completed}</span></td>
                  <td style={{ padding: '10px 16px', textAlign: 'center' }}><span style={{ color: u.overdue > 0 ? '#ef4444' : '#9ca3af', fontWeight: 700 }}>{u.overdue}</span></td>
                  <td style={{ padding: '10px 16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{ flex: 1, height: 6, background: '#f0f0f0', borderRadius: 4 }}>
                        <div style={{ height: '100%', width: u.rate + '%', background: u.rate >= 70 ? '#34c759' : u.rate >= 40 ? '#f97316' : '#ef4444', borderRadius: 4 }} />
                      </div>
                      <span style={{ fontWeight: 800, fontSize: 12, color: u.rate >= 70 ? '#34c759' : u.rate >= 40 ? '#f97316' : '#ef4444', minWidth: 32 }}>%{u.rate}</span>
                    </div>
                  </td>
                </tr>
              ))}
              {userStats.length === 0 && (
                <tr><td colSpan={5} style={{ padding: 24, textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>Veri yok</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </AdminLayout>
  );
}
